import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoopIcon from '@mui/icons-material/Loop';
import { Button, useMediaQuery } from '@mui/material';
import { Auth } from 'aws-amplify';
import { Form, Formik, FormikHelpers } from 'formik';
import routes from 'routes';
import * as Yup from 'yup'; // Import Yup for validation
import { FormInput } from 'components';
import { resetInitialValues } from './constants';
import {
  StyledField,
  StyledForgotPasswordButton,
  StyledLink,
  StyledSimpleText,
} from 'pages/Authentication/SignIn/styled';
import theme, { animations } from 'themes';
import { FormValues } from 'features/Authentication/PasswordReset/types';

// Define the shape of the form values.
const PasswordResetAmplify: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    const { username, session } = location.state || {}; // Retrieve the username and session from location state
    if (!username || !session) {
      console.error('Username or session not found in location state');
      setSubmitting(false);
      return;
    }
  
    try {
      // Retrieve the authenticated user object
      const user = await Auth.signIn(username, session);
  
      // Use the completeNewPassword method with the authenticated user
      await Auth.completeNewPassword(user, values.password);
  
      navigate(routes.auth.logIn);
    } catch (err) {
      console.error('Error completing new password', err);
      setSubmitting(false);
    }
  };

  // Validation schema for form
  const validationSchema = Yup.object({
    password: Yup.string().required('New password is required'),
    passwordConfirm: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Confirm password is required'),
  });

  return (
    <Formik<FormValues>
      initialValues={resetInitialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema} // Add validation schema
    >
      {({ isSubmitting }) => (
        <Form>
          <StyledField
            name="password"
            label="New Password"
            component={FormInput}
            margin={'normal'}
            type="password"
          />
          <StyledField
            name="passwordConfirm"
            label="Confirm Password"
            component={FormInput}
            margin={'normal'}
            type="password"
          />
          <Button
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{ mt: 2, mb: 2, textTransform: 'capitalize' }}
          >
            {isSubmitting && (
              <LoopIcon
                sx={{ animation: `${animations.spin} 2s linear infinite` }}
              />
            )}
            Change Password
          </Button>

          {isMobile ? (
            <StyledForgotPasswordButton
              size="large"
              fullWidth
              variant="contained"
              onClick={() => navigate(routes.auth.logIn)}
            >
              Go back
            </StyledForgotPasswordButton>
          ) : (
            <StyledSimpleText>
              Remembered your password?
              <StyledLink href={routes.auth.logIn}>Login Here</StyledLink>
            </StyledSimpleText>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default PasswordResetAmplify;
